import React,{useState,useEffect,useRef,useMemo} from 'react';import {Link,useNavigate,useLocation} from 'react-router-dom';import {motion,AnimatePresence} from 'framer-motion';import {useSupabaseAuth} from '../../contexts/SupabaseAuthContext';import {useSupabaseData} from '../../contexts/SupabaseDataContext';import {getBrandingAsset} from '../../services/storageService';import {isDashboardVisibleOrder} from '../../services/orderQueryService';import SafeIcon from '../../common/SafeIcon';import * as FiIcons from 'react-icons/fi';

const {FiMenu,FiX,FiShoppingBag,FiLogOut,FiSettings,FiBriefcase,FiShield,FiLoader,FiMessageCircle}=FiIcons;
const ADMIN_PATH='admin-portal';

const Navbar=()=> {
  const [isOpen,setIsOpen]=useState(false);
  const [showUserMenu,setShowUserMenu]=useState(false);
  const {user,profile,signOut}=useSupabaseAuth();
  const {messages,orders}=useSupabaseData();
  const navigate=useNavigate();
  const location=useLocation();
  const userMenuRef=useRef(null);
  const isSeller=profile?.is_seller || profile?.role==='seller';

  const unreadMessagesCount=useMemo(()=> {
    if (!messages || !user) return 0;
    return messages.filter(m=> m.receiver_id===user.id && !m.read_at).length;
  },[messages,user]);

  const activeOrdersCount=useMemo(()=> {
    if (!orders || !user) return 0;

    const closedStatuses=new Set([
      'completed',
      'cancelled',
      'declined',
      'refunded'
    ]);

    return orders.filter(o=> {
      if (!isDashboardVisibleOrder(o)) return false;
      const isMyOrder=o.buyer_id===user.id || (o.seller?.user_id===user.id);
      const normalizedStatus=String(o.status || '').trim().toLowerCase();
      const isOpen=!closedStatuses.has(normalizedStatus);
      return isMyOrder && isOpen;
    }).length;
  },[orders,user]);

  useEffect(()=> {
    const handleClickOutside=(event)=> {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown',handleClickOutside);
    return ()=> document.removeEventListener('mousedown',handleClickOutside);
  },[]);

  const handleLogout=async ()=> {
    setIsOpen(false);
    setShowUserMenu(false);
    await signOut();
    navigate('/');
  };

  const logoUrl=getBrandingAsset('logo.png');

  return (
    <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <Link to="/" className="flex items-center h-full">
            <div className="h-20 md:h-22 w-auto transition-transform hover:scale-105 flex items-center py-1">
              <img src={logoUrl} alt="SohLocal" className="h-full w-auto object-contain" />
            </div>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/sellers"
              className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                location.pathname==='/sellers'
                  ? 'text-indigo-600'
                  : 'text-slate-400 hover:text-indigo-600'
              }`}
            >
              Marketplace
            </Link>

            {user && (
              <>
                <Link
                  to="/orders"
                  className={`relative text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                    location.pathname==='/orders'
                      ? 'text-indigo-600'
                      : 'text-slate-400 hover:text-indigo-600'
                  }`}
                >
                  My Orders
                  {activeOrdersCount > 0 && (
                    <span className="absolute -top-3 -right-4 w-5 h-5 bg-indigo-600 text-white text-[9px] flex items-center justify-center rounded-full ring-2 ring-white shadow-lg shadow-indigo-100 font-black">
                      {activeOrdersCount}
                    </span>
                  )}
                </Link>

                <Link
                  to="/chat"
                  className={`relative text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                    location.pathname.startsWith('/chat')
                      ? 'text-indigo-600'
                      : 'text-slate-400 hover:text-indigo-600'
                  }`}
                >
                  Messages
                  {unreadMessagesCount > 0 && (
                    <span className="absolute -top-3 -right-4 w-5 h-5 bg-emerald-600 text-white text-[9px] flex items-center justify-center rounded-full ring-2 ring-white shadow-lg shadow-emerald-100 font-black">
                      {unreadMessagesCount}
                    </span>
                  )}
                </Link>
              </>
            )}

            {isSeller && (
              <Link
                to="/seller-dashboard"
                className="flex items-center px-5 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all border border-emerald-100 shadow-sm shadow-emerald-100/50"
              >
                <SafeIcon icon={FiBriefcase} className="mr-2 text-sm" />
                Seller Hub
              </Link>
            )}

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={()=> setShowUserMenu(!showUserMenu)}
                  className="flex items-center p-1 rounded-2xl hover:bg-slate-50 transition-all border-2 border-transparent hover:border-slate-100"
                >
                  <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center font-black text-sm uppercase shadow-lg shadow-slate-100">
                    {profile?.name?.charAt(0) || user.email?.charAt(0)}
                  </div>
                </button>

                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{opacity: 0,scale: 0.95,y: 10}}
                      animate={{opacity: 1,scale: 1,y: 0}}
                      exit={{opacity: 0,scale: 0.95,y: 10}}
                      className="absolute right-0 mt-4 w-64 bg-white rounded-3xl shadow-2xl py-2 border border-slate-50 overflow-hidden"
                    >
                      <div className="px-6 py-6 bg-slate-50 border-b border-slate-100 mb-2">
                        <p className="text-sm font-black text-slate-900 truncate">
                          {profile?.name || 'User'}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate mt-1">
                          {user.email}
                        </p>
                      </div>

                      {profile?.role==='admin' && (
                        <Link
                          to={`/${ADMIN_PATH}`}
                          onClick={()=> setShowUserMenu(false)}
                          className="flex items-center px-6 py-4 text-sm font-bold text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <SafeIcon icon={FiShield} className="mr-3" />
                          Admin Portal
                        </Link>
                      )}

                      <Link
                        to="/profile"
                        onClick={()=> setShowUserMenu(false)}
                        className="flex items-center px-6 py-4 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <SafeIcon icon={FiSettings} className="mr-3" />
                        Account Settings
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-6 py-4 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors border-t border-slate-50"
                      >
                        <SafeIcon icon={FiLogOut} className="mr-3" />
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-[10px] font-black text-slate-900 uppercase tracking-[0.2em] px-4 py-2 hover:bg-slate-50 rounded-xl transition-all"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-slate-900 text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all"
                >
                  Join Now
                </Link>
              </div>
            )}
          </div>

          <div className="md:hidden flex items-center gap-3">
            {isSeller && (
              <Link
                to="/seller-dashboard"
                className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-emerald-100"
              >
                <SafeIcon icon={FiBriefcase} />
              </Link>
            )}

            <button
              onClick={()=> setIsOpen(!isOpen)}
              className="p-2 text-slate-900 bg-slate-50 rounded-xl"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{height: 0,opacity: 0}}
            animate={{height: 'auto',opacity: 1}}
            exit={{height: 0,opacity: 0}}
            className="md:hidden border-t border-slate-100 bg-white overflow-hidden"
          >
            <div className="px-4 py-6 space-y-4">
              {isSeller && (
                <Link
                  to="/seller-dashboard"
                  onClick={()=> setIsOpen(false)}
                  className="flex items-center p-4 bg-emerald-600 text-white rounded-2xl text-sm font-black uppercase tracking-widest shadow-xl shadow-emerald-100"
                >
                  <SafeIcon icon={FiBriefcase} className="mr-3 text-lg" />
                  Seller Dashboard
                </Link>
              )}

              <Link
                to="/sellers"
                onClick={()=> setIsOpen(false)}
                className="block p-4 text-sm font-black text-slate-900 uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-all"
              >
                Marketplace
              </Link>

              {user && (
                <>
                  <Link
                    to="/orders"
                    onClick={()=> setIsOpen(false)}
                    className="flex justify-between items-center p-4 text-sm font-black text-slate-900 uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-all"
                  >
                    My Orders
                    {activeOrdersCount > 0 && (
                      <span className="bg-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-full">
                        {activeOrdersCount}
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/chat"
                    onClick={()=> setIsOpen(false)}
                    className="flex justify-between items-center p-4 text-sm font-black text-slate-900 uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-all"
                  >
                    Messages
                    {unreadMessagesCount > 0 && (
                      <span className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full">
                        {unreadMessagesCount}
                      </span>
                    )}
                  </Link>
                </>
              )}

              {user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={()=> setIsOpen(false)}
                    className="block p-4 text-sm font-black text-slate-900 uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-all"
                  >
                    Profile Settings
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="block w-full text-left p-4 text-sm font-black text-red-600 uppercase tracking-widest hover:bg-red-50 rounded-2xl transition-all"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <Link
                    to="/login"
                    onClick={()=> setIsOpen(false)}
                    className="flex items-center justify-center p-4 bg-slate-50 text-slate-900 text-sm font-black uppercase tracking-widest rounded-2xl"
                  >
                    Sign In
                  </Link>

                  <Link
                    to="/register"
                    onClick={()=> setIsOpen(false)}
                    className="flex items-center justify-center p-4 bg-indigo-600 text-white text-sm font-black uppercase tracking-widest rounded-2xl shadow-lg shadow-indigo-100"
                  >
                    Join
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
